/**
 * Theme tokens — mirrors ZiniPlaybook tokens.js + useTheme().
 *
 * Usage:
 *   import { T, useTheme, ACCENTS } from "../theme/useTheme";
 *
 *   function MyComp({ dark }) {
 *     const th = useTheme(dark);
 *     return <div style={{ background: th.surface, color: th.text }}>…</div>;
 *   }
 */

export const T = Object.freeze({
  /* Brand — Green-Leaning Petrol */
  brand:       "#1B6B82",
  brandHover:  "#175D74",
  brandSoft:   "rgba(27,107,130,0.12)",
  brandBorder: "rgba(27,107,130,0.28)",

  /* Brand Dark Mode */
  brandDark:      "#2BA09E",
  brandDarkHover: "#24938A",
  brandDarkSoft:  "rgba(43,160,158,0.12)",

  /* Sidebar Colors */
  sidebarLight: "#0F4C5C",
  sidebarDark:  "#1a5652",

  /* Status Colors - Order States */
  statusPlaced:       "#d97706",
  statusConfirmed:    "#2563eb",
  statusProcessing:   "#7c3aed",
  statusReady:        "#ea580c",
  statusOutForDelivery: "#d97706",
  statusCompleted:    "#16a34a",
  statusCancelled:    "#dc2626",

  /* Semantic Colors */
  cyan:      "#06b6d4", cyanSoft:   "rgba(6,182,212,0.10)",
  teal:      "#14b8a6", tealSoft:   "rgba(20,184,166,0.10)",
  amber:     "#f59e0b", amberSoft:  "rgba(245,158,11,0.12)",
  coral:     "#f97316", coralSoft:  "rgba(249,115,22,0.10)",
  green:     "#22c55e", greenSoft:  "rgba(34,197,94,0.10)",
  red:       "#ef4444", redSoft:    "rgba(239,68,68,0.10)",
  violet:    "#7c3aed", violetSoft: "rgba(124,58,237,0.12)",

  /* Light palette */
  lCanvas:    "#f8fafc",
  lSurface:   "#ffffff",
  lTopbar:    "#ffffff",
  lSidebar:   "#0F4C5C",
  lElevated:  "#ffffff",
  lMuted:     "#f1f5f9",
  lBorder:    "rgba(51,65,85,0.10)",
  lBorderMd:  "rgba(51,65,85,0.18)",
  lBorderStrong: "rgba(51,65,85,0.28)",
  lText:      "#0f172a",
  lTextSec:   "#475569",
  lTextMuted: "#94a3b8",
  lTextDisabled: "#cbd5e1",
  lIcon:      "#475569",
  lIconSec:   "#94a3b8",
  lIconMuted: "#cbd5e1",

  /* Dark palette – layered surfaces from deepest to lightest */
  dCanvas:    "#0a0d12",
  dSidebar:   "#1a5652",
  dTopbar:    "#0c1017",
  dSurface:   "#111318",
  dElevated:  "#181c23",
  dMuted:     "#1e2330",
  dBorder:    "rgba(255,255,255,0.08)",
  dBorderMd:  "rgba(255,255,255,0.14)",
  dBorderStrong: "rgba(255,255,255,0.20)",
  dText:      "#f1f5f9",
  dTextSec:   "#b0bec5",
  dTextMuted: "#64748b",
  dTextDisabled: "#475569",
  dIcon:      "#b0bec5",
  dIconSec:   "#64748b",
  dIconMuted: "#475569",

  /* Spacing Scale */
  spacingXs:   "4px",
  spacingSm:   "8px",
  spacingMd:   "12px",
  spacingLg:   "16px",
  spacingXl:   "20px",
  spacing2xl:  "24px",
  spacing3xl:  "32px",
  spacing4xl:  "40px",

  /* Gap Tokens */
  gapXs:  "4px",
  gapSm:  "8px",
  gapMd:  "12px",
  gapLg:  "16px",
  gapXl:  "20px",

  /* Padding Tokens */
  paddingPageDesktop: "32px",
  paddingPageTablet:  "24px",
  paddingPageMobile:  "16px",

  /* Typography Scale */
  /* Heading 1 */
  typographyH1Size: "24px",
  typographyH1Weight: 600,
  typographyH1LineHeight: 1.3,

  /* Heading 2 */
  typographyH2Size: "20px",
  typographyH2Weight: 600,
  typographyH2LineHeight: 1.35,

  /* Heading 3 */
  typographyH3Size: "16px",
  typographyH3Weight: 600,
  typographyH3LineHeight: 1.4,

  /* Body — Regular paragraph text */
  typographyBodySize: "14px",
  typographyBodyWeight: 400,
  typographyBodyLineHeight: 1.5,

  /* Body Small */
  typographyBodySmSize: "13px",
  typographyBodySmWeight: 400,
  typographyBodySmLineHeight: 1.5,

  /* Label — Form labels, badges, tags */
  typographyLabelSize: "12px",
  typographyLabelWeight: 500,
  typographyLabelLineHeight: 1.4,

  /* Caption — Small text, helper text, timestamps */
  typographyCaptionSize: "11px",
  typographyCaptionWeight: 400,
  typographyCaptionLineHeight: 1.4,

  /* Button Text */
  typographyButtonSize: "12px",
  typographyButtonWeight: 500,
  typographyButtonLineHeight: 1.4,

  /* Motion */
  fast:   "150ms",
  mid:    "220ms",
  slow:   "320ms",
  ease:   "cubic-bezier(0.4,0,0.2,1)",
  spring: "cubic-bezier(0.34,1.56,0.64,1)",

  rsm: "6px",
  rmd: "10px",
  rlg: "16px",
  rxl: "24px",
});

/**
 * Returns a derived theme object for the current dark/light mode.
 *
 * Semantic surfaces (darkest → lightest):
 *   canvas → sidebar → topbar → surface → elevated → muted
 *
 * @param {boolean} dark
 * @returns {{
 *   bg, canvas, sidebar, topbar, surface, elevated, muted,
 *   border, borderMd, borderStrong,
 *   text, textSec, textMuted, textDisabled, textInverse,
 *   icon, iconSecondary, iconMuted, iconAccent, iconDanger,
 *   shadowSm, shadowMd, shadowLg,
 *   dark, tr, trSlow, motion: { hover, card, dropdown, modal }
 * }}
 */
export function useTheme(dark) {
  const s = (light, darkVal) => dark ? darkVal : light;
  return {
    /* Surfaces */
    bg:        s(T.lCanvas, T.dCanvas),
    canvas:    s(T.lCanvas, T.dCanvas),
    sidebar:   s(T.lSidebar, T.dSidebar),
    topbar:    s(T.lTopbar, T.dTopbar),
    surface:   s(T.lSurface, T.dSurface),
    elevated:  s(T.lElevated, T.dElevated),
    muted:     s(T.lMuted, T.dMuted),

    /* Borders */
    border:         s(T.lBorder, T.dBorder),
    borderMd:       s(T.lBorderMd, T.dBorderMd),
    borderStrong:   s(T.lBorderStrong, T.dBorderStrong),
    borderSubtle:   dark ? "rgba(255,255,255,0.04)" : "rgba(51,65,85,0.04)",

    /* Typography */
    text:           s(T.lText, T.dText),
    textSec:        s(T.lTextSec, T.dTextSec),
    textMuted:      s(T.lTextMuted, T.dTextMuted),
    textDisabled:   s(T.lTextDisabled, T.dTextDisabled),
    textInverse:    dark ? T.dCanvas : "#ffffff",

    /* Icons */
    icon:           s(T.lIcon, T.dIcon),
    iconSecondary:  s(T.lIconSec, T.dIconSec),
    iconMuted:      s(T.lIconMuted, T.dIconMuted),
    iconAccent:     T.brand,
    iconDanger:     T.red,

    /* Shadows */
    shadowSm: dark ? "0 1px 2px rgba(0,0,0,0.35)" : "0 1px 3px rgba(0,0,0,0.06)",
    shadowMd: dark ? "0 4px 12px rgba(0,0,0,0.40)" : "0 4px 12px rgba(0,0,0,0.08)",
    shadowLg: dark ? "0 8px 24px rgba(0,0,0,0.50)" : "0 8px 24px rgba(0,0,0,0.10)",

    /* Typography */
    typoH1: {
      size: T.typographyH1Size,
      weight: T.typographyH1Weight,
      lineHeight: T.typographyH1LineHeight,
    },
    typoH2: {
      size: T.typographyH2Size,
      weight: T.typographyH2Weight,
      lineHeight: T.typographyH2LineHeight,
    },
    typoH3: {
      size: T.typographyH3Size,
      weight: T.typographyH3Weight,
      lineHeight: T.typographyH3LineHeight,
    },
    typoBody: {
      size: T.typographyBodySize,
      weight: T.typographyBodyWeight,
      lineHeight: T.typographyBodyLineHeight,
    },
    typoBodySm: {
      size: T.typographyBodySmSize,
      weight: T.typographyBodySmWeight,
      lineHeight: T.typographyBodySmLineHeight,
    },
    typoLabel: {
      size: T.typographyLabelSize,
      weight: T.typographyLabelWeight,
      lineHeight: T.typographyLabelLineHeight,
    },
    typoCaption: {
      size: T.typographyCaptionSize,
      weight: T.typographyCaptionWeight,
      lineHeight: T.typographyCaptionLineHeight,
    },
    typoButton: {
      size: T.typographyButtonSize,
      weight: T.typographyButtonWeight,
      lineHeight: T.typographyButtonLineHeight,
    },

    dark,
    tr:     `all ${T.mid} ${T.ease}`,
    trSlow: `all ${T.slow} ${T.ease}`,

    /* Semantic motion tokens */
    motion: {
      hover:     `all 150ms ${T.ease}`,
      card:      `all 200ms ${T.ease}`,
      dropdown:  `all 220ms ${T.ease}`,
      modal:     `all 320ms ${T.ease}`,
    },
  };
}

export const ACCENTS = Object.freeze({
  brand:  { solid: T.brand,  soft: T.brandSoft  },
  cyan:   { solid: T.cyan,   soft: T.cyanSoft   },
  teal:   { solid: T.teal,   soft: T.tealSoft   },
  amber:  { solid: T.amber,  soft: T.amberSoft  },
  coral:  { solid: T.coral,  soft: T.coralSoft  },
  green:  { solid: T.green,  soft: T.greenSoft  },
  red:    { solid: T.red,    soft: T.redSoft    },
  violet: { solid: T.violet, soft: T.violetSoft },
});
